import { z } from "zod";
import { RubricDimensionSchema, TrackSchema } from "./primitives.js";
/**
 * Learner and progression contracts (ADR 0006, ADR 0008; docs/RECONCILIATION.md
 * rows 8/12/13/14). Approaches and skill estimates are inferred by
 * ProfileExtractor from onboarding prose, not chosen — see CLAUDE.md Law 6.
 */
export const ApproachSchema = z.enum([
    "DATA_FIRST",
    "CLIENT_WHISPERER",
    "HYPOTHESIS_DRIVEN",
    "FIRST_PRINCIPLES",
    "OPERATOR",
    "CONTRARIAN",
]);
export const ExperienceLevelSchema = z.enum(["STUDENT", "EARLY_CAREER", "EXPERIENCED", "SENIOR"]);
/**
 * `confirmed_by_user` is `boolean`, not defaulted — an unconfirmed profile is
 * a distinct state a caller must handle, not a silently-assumed default (Law
 * 4: schema-validate every model response, confirm extraction back to the
 * user).
 */
export const LearnerProfileSchema = z.strictObject({
    learner_id: z.string().min(1),
    name: z.string().min(1),
    target_functions: z.array(z.string().min(1)),
    target_firms: z.array(z.string().min(1)),
    self_identified_gaps: z.array(z.string().min(1)),
    experience_level: ExperienceLevelSchema,
    /**
     * Zero, one or two Approaches (onboarding pass, 2026-09-13). Two labels
     * inferred from three sentences of onboarding prose were where thin
     * evidence turned into confident nonsense; an approach now reaches the
     * profile only with a verbatim quote from the learner behind it, and
     * none at all is a valid, honest state. The calibration case is where
     * how a learner actually works is learned.
     */
    approaches: z.array(ApproachSchema).max(2),
    /**
     * True until a finished engagement has shown how the learner actually
     * works. While provisional, every reader treats the approaches as a
     * first guess to be spoken of as such, never as a finding.
     */
    approaches_provisional: z.boolean(),
    extracted_from_transcript: z.string().min(1),
    confirmed_by_user: z.boolean(),
});
/**
 * THE INTAKE (onboarding pass, 2026-09-13). The profile is no longer
 * inferred in one shot from one paragraph. The learner's prose is read
 * into a DRAFT in which every field is either absent or backed by a
 * verbatim quote from what they wrote; deterministic code decides which
 * required field is still missing and asks for exactly that, in an
 * authored sentence; the learner's answer, or a correction to the
 * read-back, is folded into the same draft. The model never chooses what
 * to ask next (Law 1) and never fills a field it has no words for (Law 4).
 */
export const ProfileFieldSchema = z.enum([
    "name",
    "experience_level",
    "target_functions",
    "target_firms",
    "self_identified_gaps",
    "approaches",
    /** The one of the six skills the learner most wants to work on first (AJ intake, 2026-09-24): their first focus. */
    "focus_skill",
]);
/** One thing the draft holds and the learner's own words it was taken from. */
export const ProfileEvidenceSchema = z.strictObject({
    field: ProfileFieldSchema,
    value: z.string().min(1),
    /** Verbatim from the learner's lines; verified as a substring before it is kept. */
    quote: z.string().min(1),
});
export const ProfileDraftSchema = z.strictObject({
    name: z.string().min(1).nullable(),
    experience_level: ExperienceLevelSchema.nullable(),
    target_functions: z.array(z.string().min(1)),
    target_firms: z.array(z.string().min(1)),
    self_identified_gaps: z.array(z.string().min(1)),
    approaches: z.array(ApproachSchema).max(2),
    /**
     * The skill the learner said they most want to work on, mapped by the
     * extractor from their words and quoted like every other value. It becomes
     * the first focus paper on the desk and a step in the first plan; it is
     * never written to the profile row itself.
     */
    focus_skill: RubricDimensionSchema.nullable(),
    evidence: z.array(ProfileEvidenceSchema),
});
/**
 * What the intake still needs, chosen by code. `goals` covers the three
 * list fields together: it is asked once, and an empty answer is allowed.
 * `focus` asks directly for the one skill to work on first, once (AJ intake,
 * 2026-09-24); a learner who still names none gets the desk's own empty
 * focus paper rather than an invented one. `options`, when present, are the
 * closed set the learner may pick from without a model call (the four
 * experience levels); `plain` means the model has had its allowed rounds and
 * the answer will be kept verbatim.
 */
export const ProfileQuestionFieldSchema = z.enum(["name", "experience_level", "goals", "focus"]);
export const ProfileQuestionSchema = z.strictObject({
    field: ProfileQuestionFieldSchema,
    text: z.string().min(1),
    options: z.array(ExperienceLevelSchema).nullable(),
    plain: z.boolean(),
});
/** One line of the intake conversation, kept so the extractor reads the whole exchange every time (Law 4: re-inject, never rely on history). */
export const ProfileIntakeLineSchema = z.strictObject({
    from: z.enum(["coach", "learner"]),
    text: z.string().min(1),
});
export const ProfileIntakeTranscriptSchema = z.array(ProfileIntakeLineSchema);
/**
 * THE REHEARSAL (onboarding pass, idea 9). Before the first case the coach
 * sets one small scene and asks for one move, then says which of the three
 * parts of a good move were there: what the learner thought, what they had
 * seen that made them think it, and what they chose to do. Each part is
 * either absent or a verbatim quote, verified in code. The reflection the
 * learner reads is authored copy chosen by which parts were present.
 */
/**
 * THE COACH'S INTRODUCTION (AJ intake, 2026-09-24; Andre's script). Typed
 * onto the front door paragraph by paragraph the first time a learner
 * arrives, and kept in their conversation as the coach's first words. One
 * source, here, so the client that types it and the server that keeps it
 * cannot drift. `*word*` marks emphasis for the client to render; nothing
 * else in it is markup. The exclamation mark in the first line is the one
 * the coach is allowed: the script is authored, and the model's own lines
 * still carry none.
 */
export const AJ_INTRO = [
    "Hey there! My name's AJ, or Automated Jack. You can call me AJ or Jack or… whatever you'd like I suppose, since I'm only a construct. I'm your coach/guide through this unique software. You may find it different than any other practice software you've used.",
    "Basically, you and our AI Narrator will tell a story together. The Narrator will give you a business problem, and you will have to describe how you will go about solving it. Don't expect these cases to be simple, easy, or leading. You will encounter clients with huge blind spots, executives with hidden motives, piles of documents that are hard to navigate, gatekeepers with a temper… all things you will have to deal with in the real world. These cases won't take it easy on you. The kicker? All of the businesses you're trying to help serve real people with real needs: your findings and recommendations have real business and emotional consequences.",
    "Well, not *real* real. They're only stories after all.",
    "I read every engagement you finish here, and I stay with you between them: keeping track of your personal development, and suggesting what to try next.",
    "But that's enough about me. Tell me about yourself, then I'll put a few things on your desk. What brings you here?",
];
/** The one rehearsal scene, authored outside every case so nothing in it is a fact a learner could carry into play. */
export const REHEARSAL_SCENE = "A client's finance lead tells you the numbers are fine and hands you a one-page summary. The date at the top is from last spring. The board meets in nine days.";
export const RehearsalPartsSchema = z.strictObject({
    thinking: z.string().min(1).nullable(),
    evidence: z.string().min(1).nullable(),
    action: z.string().min(1).nullable(),
});
const RubricScoreSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).nullable();
export const CalibrationBaselineSchema = z.strictObject({
    learner_id: z.string().min(1),
    case_id: z.string().min(1),
    timestamp: z.iso.datetime(),
    baseline: z.strictObject({
        structure: RubricScoreSchema,
        quantitative: RubricScoreSchema,
        judgment: RubricScoreSchema,
        communication: RubricScoreSchema,
        ambiguity_tolerance: RubricScoreSchema,
        calibration: RubricScoreSchema,
    }),
});
const TrackProgressSchema = z.strictObject({
    track: TrackSchema,
    unlocked: z.boolean(),
    completed_case_ids: z.array(z.string().min(1)),
});
export const ProgressionSchema = z.strictObject({
    learner_id: z.string().min(1),
    tracks: z.array(TrackProgressSchema).length(5),
});
/**
 * Every consumer must handle the anonymous case explicitly — there is no
 * shared base type a GuestIdentity and UserIdentity are both quietly
 * assignable to.
 */
export const IdentitySchema = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("guest"),
        guest_id: z.string().min(1),
        created_at: z.iso.datetime(),
        retention_expires_at: z.iso.datetime(),
    }),
    z.strictObject({
        kind: z.literal("user"),
        user_id: z.string().min(1),
        email: z.email(),
        claimed_from_guest_id: z.string().min(1).nullable(),
    }),
]);
//# sourceMappingURL=learner.js.map